/**
 * The reads, draft and save behind the Change contest rules dialog
 * (ISSUE-1041 R1, R2, R3, R6). Opened by `?dialog=edit-contest-rules&contestId=`,
 * the URL pattern `PastSeasonsDialogContainer.tsx` follows.
 *
 * Prefills from `/contest/get`, sends only the changed fields to
 * `/contest/update`, and hands the saved message (which round the change
 * starts in, SUR-R73) back to the card. Every refusal goes to the shared error
 * modal with the `contestRulesEdit` context, and the stored rules stay as they
 * were because the server wrote nothing.
 */
import {
  useContestGet,
  useContestUpdate,
  useRoundList,
} from "@sportsmet/contest-sdk/private";
import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import {
  missingSurvivorRulesItems,
  type typeSurvivorRulesDraft,
} from "../createPool/survivorRulesDraft";
import {
  CONTEST_RULES_DATES_LOCKED_NOTE,
  contestRulesSavedMessage,
  EDIT_CONTEST_RULES_DIALOG_NAME,
  isContestStatusEnded,
} from "./contestRulesCardState";
import {
  areContestDatesLocked,
  buildContestRulesPatch,
  isContestRulesDraftDirty,
  survivorRulesDraftFromConfig,
} from "./contestRulesEditDraft";

export type typeEditContestRulesView =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "ended" }
  | {
      kind: "ready";
      contestName: string;
      draft: typeSurvivorRulesDraft;
      datesLockedNote: string | null;
      missingItems: readonly string[];
      isDirty: boolean;
      isSaveEnabled: boolean;
      isSaving: boolean;
    };

export type typeUseEditContestRulesResult = {
  isOpen: boolean;
  view: typeEditContestRulesView;
  setDraft: (next: typeSurvivorRulesDraft) => void;
  save: () => Promise<void>;
  close: () => void;
  retry: () => void;
};

const CONTEST_RULES_QUERY_PREFIX = [
  "post:contest:contestRulesGet",
  "svc:contest-api",
];

export function useEditContestRules(input: {
  poolId: string;
  onSaved: (message: string) => void;
}): typeUseEditContestRulesResult {
  const { poolId, onSaved } = input;
  const overlay = useOverlay();
  const search = useSearch({ strict: false });
  const queryClient = useQueryClient();

  const isOpen = overlay.isOpen("dialog", EDIT_CONTEST_RULES_DIALOG_NAME);
  const contestId =
    isOpen && typeof search.contestId === "string" ? search.contestId : "";

  const contest = useContestGet(
    { poolId, contestId },
    { enabled: contestId !== "", staleTime: 0, refetchOnMount: "always" },
  );
  const rounds = useRoundList(
    { poolId, contestId, limit: 500 },
    { enabled: contestId !== "" },
  );
  const update = useContestUpdate();

  const contestData = contest.data?.data ?? null;
  const baseline = useMemo(
    () =>
      contestData ? survivorRulesDraftFromConfig(contestData.config) : null,
    [contest.data],
  );

  const [draft, setDraft] = useState<typeSurvivorRulesDraft | null>(null);

  // A fresh dialog (or another contest) starts from the server's rules again.
  useEffect(() => {
    setDraft(null);
  }, [contestId, isOpen]);

  // The draft follows the server's rules until the Commissioner edits
  // something: a refetch that lands while the form is untouched moves the
  // form with it, and never overwrites a change in progress.
  const isDraftEmpty = draft === null;
  const followedBaseline = useRef<typeSurvivorRulesDraft | null>(null);
  useEffect(() => {
    if (baseline === null) return;
    const previous = followedBaseline.current;
    followedBaseline.current = baseline;
    setDraft((current) =>
      current === null ||
      (previous !== null &&
        !isContestRulesDraftDirty({ baseline: previous, next: current }))
        ? baseline
        : current,
    );
  }, [baseline, isDraftEmpty]);

  const roundItems = rounds.data?.data?.items ?? [];
  // 0 on a failed round read, which leaves the server as the check.
  const roundCount = rounds.isLoading ? null : roundItems.length;

  const patch =
    baseline !== null && draft !== null
      ? buildContestRulesPatch({ baseline, next: draft })
      : null;

  function close(): void {
    overlay.close({ kind: "dialog", clearParams: ["contestId"] });
  }

  function retry(): void {
    void contest.refetch();
  }

  async function save(): Promise<void> {
    if (patch === null || Object.keys(patch).length === 0) return;
    if (baseline === null || draft === null) return;
    try {
      const result = await update.mutateAsync({ poolId, contestId, ...patch });
      if (showApiError(result, "contestRulesEdit")) {
        // CONFLICT only ever means contestStarted: a round was cut while the
        // dialog was open. Lock the dates and put them back, so the next Save
        // keeps every other change.
        if (extractErrorCode(result) === "CONFLICT") {
          void rounds.refetch();
          setDraft({
            ...draft,
            startDate: baseline.startDate,
            endDate: baseline.endDate,
            endDateMode: baseline.endDateMode,
          });
        }
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: CONTEST_RULES_QUERY_PREFIX,
      });
      onSaved(
        contestRulesSavedMessage({
          effectiveFromRoundNumber:
            result.data?.effectiveFromRoundNumber ?? null,
          hasRounds: roundItems.length > 0,
        }),
      );
      close();
    } catch {
      showOfflineError();
    }
  }

  let view: typeEditContestRulesView;
  if (contest.isError || extractErrorCode(contest.data) !== null) {
    view = { kind: "failed" };
  } else if (contest.isLoading || contestData === null || draft === null) {
    view = { kind: "loading" };
  } else if (isContestStatusEnded(contestData.status)) {
    view = { kind: "ended" };
  } else {
    view = {
      kind: "ready",
      contestName: contestData.name,
      draft,
      datesLockedNote: areContestDatesLocked({ roundCount })
        ? CONTEST_RULES_DATES_LOCKED_NOTE
        : null,
      missingItems: missingSurvivorRulesItems(draft),
      isDirty:
        baseline !== null &&
        isContestRulesDraftDirty({ baseline, next: draft }),
      isSaveEnabled:
        patch !== null && Object.keys(patch).length > 0 && !update.isPending,
      isSaving: update.isPending,
    };
  }

  return { isOpen, view, setDraft, save, close, retry };
}
